'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { decodeJwtPayload } from '@/lib/heimdall-admin-token';
export interface CSMTenant {
  id: string;
  tenant_name: string;
  tenant_code: string;
  status: string;
}
export interface CSMTenantAccess {
  id: string;
  csm_user_id: string;
  email: string;
  is_active: boolean;
  granted_at: string;
  tenant: CSMTenant;
}
export interface CSMContextType {
  // User role info from token
  isCSM: boolean;
  isSuperAdmin: boolean;
  role: string | null;
  canManageOrgs: boolean;

  // Current tenant info from token
  currentTenantId: string | null;
  currentTenantName: string | null;
  currentTenantCode: string | null;
  currentOrgId: string | null;
  currentOrgName: string | null;

  // Available tenants
  accessibleTenants: CSMTenantAccess[] | null;
  isLoadingTenants: boolean;

  // Actions
  loadAccessibleTenants: () => Promise<void>;
  switchTenant: (tenantId: string, refreshToken: string) => Promise<void>;

  // State
  isLoaded: boolean;
}
export const CSMContext = createContext<CSMContextType | null>(null);
export function useCSM() {
  const context = useContext(CSMContext);
  if (!context) {
    throw new Error('useCSM must be used within a CSMProvider');
  }
  return context;
}
interface CSMProviderProps {
  children: ReactNode;
  token: string | null; // ID token from auth
}
export function CSMProvider({
  children,
  token
}: CSMProviderProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [accessibleTenants, setAccessibleTenants] = useState<CSMTenantAccess[] | null>(null);
  const [isLoadingTenants, setIsLoadingTenants] = useState(false);

  // Decode token to get role and tenant info
  const decodeToken = (token: string | null) => {
    if (!token) {
      return {
        role: null,
        isCSM: false,
        isSuperAdmin: false,
        currentTenantId: null,
        currentTenantName: null,
        currentTenantCode: null,
        currentOrgId: null,
        currentOrgName: null,
        canManageOrgs: false
      };
    }
    try {
      const payload = decodeJwtPayload(token);
      if (!payload) {
        return {
          role: null,
          isCSM: false,
          isSuperAdmin: false,
          currentTenantId: null,
          currentTenantName: null,
          currentTenantCode: null,
          currentOrgId: null,
          currentOrgName: null,
          canManageOrgs: false
        };
      }
      const role = payload['custom:role'] as string || null;
      const isSuperAdmin = role === 'super_admin' || payload['custom:is_super_admin'] === 'true';
      // CSM includes both explicit CSM role and super admins
      const isCSM = role === 'csm' || role === 'super_admin' || isSuperAdmin;
      // Match backend: super_admin unconditionally, or CSM with custom:misc3='true' (DB can_manage_organizations checked by API)
      const canManageOrgs = isSuperAdmin || isCSM && payload['custom:misc3'] === 'true';
      return {
        role,
        isCSM,
        isSuperAdmin,
        currentTenantId: payload['custom:misc2'] as string || null,
        // Tenant ID with dashes
        currentTenantName: payload['custom:tenant_name'] as string || null,
        currentTenantCode: payload['custom:tenant_name'] as string || null,
        // Same as tenant_name
        currentOrgId: payload['custom:misc1'] as string || null,
        // Org ID with dashes
        currentOrgName: payload['custom:org_name'] as string || null,
        canManageOrgs
      };
    } catch (error) {
      console.error('Failed to decode token:', error);
      return {
        role: null,
        isCSM: false,
        isSuperAdmin: false,
        currentTenantId: null,
        currentTenantName: null,
        currentTenantCode: null,
        currentOrgId: null,
        currentOrgName: null,
        canManageOrgs: false
      };
    }
  };
  const tokenData = decodeToken(token);

  // Mark context as loaded once we have a token (role/tenant info is from decode).
  // Org/tenant tabs use isLoaded to avoid showing loading forever when tenant switcher is off.
  useEffect(() => {
    if (token) {
      setIsLoaded(true);
    }
  }, [token]);
  const loadAccessibleTenants = async () => {
    if (!token || !tokenData.isCSM) {
      return;
    }
    try {
      setIsLoadingTenants(true);

      // Get the Cognito access token from ALB headers (not the ALB-signed token)
      // The x-amzn-oidc-accesstoken is the real Cognito token that Heimdall can verify
      // The x-amzn-oidc-data (which is what we have in `token`) is ALB-signed and can't be verified by Cognito JWKS
      const albInfo = await fetch('/api/auth/alb-info');
      const albData = await albInfo.json();
      // Try access token first, fallback to ID token if access token is not available
      const cognitoAccessToken = albData.tokens?.rawAccessToken || albData.tokens?.rawIdToken;
      if (!cognitoAccessToken || cognitoAccessToken.trim() === '') {
        console.error('[CSMContext] No Cognito token available', {
          hasRawAccessToken: !!albData.tokens?.rawAccessToken,
          hasRawIdToken: !!albData.tokens?.rawIdToken,
          tokens: albData.tokens
        });
        throw new Error('No Cognito access token available');
      }
      const response = await fetch('/api/csm/tenants?limit=1000', {
        headers: {
          'Authorization': `Bearer ${cognitoAccessToken}`
        }
      });
      if (!response.ok) {
        throw new Error('Failed to load accessible tenants');
      }
      const data = await response.json();
      const tenants = data.tenants || [];
      console.log('Loaded accessible tenants:', tenants.map((t: any) => ({
        id: t.tenant?.id,
        name: t.tenant?.tenant_name,
        code: t.tenant?.tenant_code
      })));
      setAccessibleTenants(tenants);
    } catch (error) {
      console.error('Error loading accessible tenants:', error);
      setAccessibleTenants([]);
    } finally {
      setIsLoadingTenants(false);
    }
  };
  const switchTenant = async (tenantId: string, refreshToken: string) => {
    if (!token) {
      throw new Error('No auth token available');
    }

    // Get the Cognito access token from ALB headers
    const albInfo = await fetch('/api/auth/alb-info');
    const albData = await albInfo.json();
    // Try access token first, fallback to ID token if access token is not available
    const cognitoAccessToken = albData.tokens?.rawAccessToken || albData.tokens?.rawIdToken;
    if (!cognitoAccessToken || cognitoAccessToken.trim() === '') {
      console.error('[CSMContext switchTenant] No Cognito token available', {
        hasRawAccessToken: !!albData.tokens?.rawAccessToken,
        hasRawIdToken: !!albData.tokens?.rawIdToken,
        tokens: albData.tokens
      });
      throw new Error('No Cognito access token available');
    }

    // In ALB authentication, we don't have access to the refresh token
    // Token is sent in Authorization header (extracted by backend from headers)
    // Heimdall will update the Cognito user attribute with the new tenant
    const requestBody: any = {
      tenant_id: tenantId
    };

    // Only include refresh_token if it's actually provided
    if (refreshToken && refreshToken.trim() !== '') {
      requestBody.refresh_token = refreshToken;
    }
    const response = await fetch('/api/csm/switch-tenant', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${cognitoAccessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to switch tenant');
    }
    const result = await response.json();

    // Use redirect_url_with_logout to force ALB session clear and re-auth
    // This is critical for same-org tenant switches where the domain stays the same
    if (result.redirect_url_with_logout) {
      window.location.href = result.redirect_url_with_logout;
    } else if (result.redirect_url) {
      // Fallback to regular redirect (for backwards compatibility)
      window.location.href = result.redirect_url;
    } else if (result.id_token) {
      // Legacy behavior: Store new token and reload
      window.location.reload();
    } else {
      // TP-2048: Fallback redirect if backend returns unexpected response format
      // This prevents stuck UI state when using programmatic tenant switching
      console.warn('[CSMContext] No redirect URL in response, falling back to current page', result);
      window.location.href = window.location.pathname + window.location.search;
    }
    return result;
  };
  const value: CSMContextType = {
    ...tokenData,
    accessibleTenants,
    isLoadingTenants,
    loadAccessibleTenants,
    switchTenant,
    isLoaded
  };
  return <CSMContext.Provider value={value} data-sentry-element="unknown" data-sentry-component="CSMProvider" data-sentry-source-file="CSMContext.tsx">
      {children}
    </CSMContext.Provider>;
}